import React, { useState } from 'react';
import { 
  Play, 
  Square, 
  RotateCcw, 
  Trash2, 
  Tag, 
  Calendar,
  MapPin,
  Clock,
  ExternalLink,
  Activity
} from 'lucide-react';
import ActionHistory from './ActionHistory';
import { authService } from '../services/authService';

const InstanceCard = ({ instance, selected, onSelect, onAction }) => {
  const [actionLoading, setActionLoading] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  // 에러 메시지를 사용자 친화적으로 변환하는 함수
  const getDisplayErrorMessage = (error) => {
    // 문자열인 경우 그대로 반환
    if (typeof error === 'string') {
      return error;
    }
    
    // 구조화된 에러 객체인 경우
    if (error && typeof error === 'object') {
      // AWS 에러의 경우 details.awsErrorMessage 우선 사용
      if (error.details?.awsErrorMessage) {
        const awsMessage = error.details.awsErrorMessage;
        
        // AWS 종료 방지 에러 한국어 변환
        if (awsMessage.includes('disableApiTermination')) {
          return '이 인스턴스는 종료 방지가 설정되어 있습니다. 인스턴스 설정에서 종료 방지를 해제한 후 다시 시도해주세요.';
        }
        
        if (awsMessage.includes('OperationNotPermitted')) {
          return '작업이 허용되지 않습니다. 인스턴스 상태 또는 권한을 확인해주세요.';
        }
        
        return awsMessage;
      }
      
      // 일반적인 에러 메시지
      if (error.message) {
        return error.message;
      }
    }
    
    return null;
  };

  const handleAction = async (action) => {
    if (actionLoading) return;
    
    // 권한 확인 (클라이언트 사이드 2차 검증)
    if (action === 'terminate' && !isAdmin) {
      alert('⚠️ 관리자만 인스턴스를 종료할 수 있습니다.');
      return;
    }
    
    const confirmMessage = `인스턴스 ${instance.instanceId}를 ${action}하시겠습니까?`;
    if (action === 'terminate') {
      // 2단계 확인 (관리자용)
      const instanceName = instance.tags?.Name || instance.instanceId;
      if (!window.confirm(`⚠️ 주의: 인스턴스를 종료하면 복구할 수 없습니다. ${confirmMessage}`)) {
        return;
      }
      // 추가 확인 단계
      const confirmText = prompt(`인스턴스를 영구 삭제하려면 "${instanceName}"를 입력하세요:`);
      if (confirmText !== instanceName) {
        alert('인스턴스 이름이 일치하지 않습니다. 종료가 취소되었습니다.');
        return;
      }
    } else if (!window.confirm(confirmMessage)) {
      return;
    }

    setActionLoading(action);
    
    try {
      const result = await onAction(instance.instanceId, action);
      if (!result.success) {
        const errorMessage = getDisplayErrorMessage(result.error) || `${action} 작업에 실패했습니다.`;
        alert(errorMessage);
      }
    } catch (error) {
      const errorMessage = getDisplayErrorMessage(error) || error.message || `작업 실행 중 오류가 발생했습니다.`;
      alert(errorMessage);
    } finally {
      setActionLoading('');
    }
  };

  const getStatusColor = (state) => {
    switch (state) {
      case 'running': return 'bg-green-100 text-green-800';
      case 'stopped': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'stopping': return 'bg-orange-100 text-orange-800';
      case 'terminated': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 권한 기반 액션 제어
  const isAdmin = authService.isUserAdmin();
  
  const canStart = ['stopped'].includes(instance.state);
  const canStop = ['running'].includes(instance.state);
  const canReboot = ['running'].includes(instance.state);
  const canTerminate = isAdmin && !['terminated', 'terminating'].includes(instance.state); // 🔒 관리자만

  return (
    <div className={`instance-card ${
      selected ? 'ring-2 ring-blue-500 border-blue-200' : ''
    }`}>
      {/* 헤더 */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(instance.instanceId, e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {instance.tags?.Name || instance.instanceId}
            </h3>
            <p className="text-sm text-gray-500">{instance.instanceId}</p>
          </div>
        </div>
        
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(instance.state)}`}>
          {instance.state}
        </span>
      </div>

      {/* 기본 정보 */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <MapPin size={16} />
          <span>{instance.instanceType} • {instance.availabilityZone}</span>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Clock size={16} />
          <span>시작: {formatDate(instance.launchTime)}</span>
        </div>
        
        {instance.publicIpAddress && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <ExternalLink size={16} />
            <span>Public: {instance.publicIpAddress}</span>
          </div>
        )}
        
        {instance.privateIpAddress && (
          <div className="text-sm text-gray-600 ml-6">
            Private: {instance.privateIpAddress}
          </div>
        )}
      </div>

      {/* 태그 */}
      {Object.keys(instance.tags || {}).length > 0 && (
        <div className="mb-4">
          <div className="flex items-center space-x-1 mb-2">
            <Tag size={16} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-700">태그</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(instance.tags).map(([key, value]) => (
              <span
                key={key}
                className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
              >
                {key}: {value}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 스케줄 정보 */}
      {instance.metadata?.schedule?.Enabled && (
        <div className="mb-4 p-2 bg-blue-50 rounded">
          <div className="flex items-center space-x-1 mb-1">
            <Calendar size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-700">스케줄 활성</span>
          </div>
          <p className="text-xs text-blue-600">
            {instance.metadata.schedule.StartTime} - {instance.metadata.schedule.StopTime}
          </p>
        </div>
      )}

      {/* 작업 버튼들 */}
      <div className="flex flex-wrap gap-2">
        {canStart && (
          <button
            onClick={() => handleAction('start')}
            disabled={actionLoading === 'start'}
            className="btn-success flex items-center space-x-1 text-sm py-1 px-3"
          >
            {actionLoading === 'start' ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Play size={14} />
            )}
            <span>시작</span>
          </button>
        )}
        
        {canStop && (
          <button
            onClick={() => handleAction('stop')}
            disabled={actionLoading === 'stop'}
            className="btn-danger flex items-center space-x-1 text-sm py-1 px-3"
          >
            {actionLoading === 'stop' ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Square size={14} />
            )}
            <span>중지</span>
          </button>
        )}
        
        {canReboot && (
          <button
            onClick={() => handleAction('reboot')}
            disabled={actionLoading === 'reboot'}
            className="btn-secondary flex items-center space-x-1 text-sm py-1 px-3"
          >
            {actionLoading === 'reboot' ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
            ) : (
              <RotateCcw size={14} />
            )}
            <span>재시작</span>
          </button>
        )}
        
        {canTerminate && (
          <button
            onClick={() => handleAction('terminate')}
            disabled={actionLoading === 'terminate'}
            className="text-red-600 hover:text-red-700 flex items-center space-x-1 text-sm py-1 px-3 border border-red-300 hover:border-red-400 rounded transition-colors"
          >
            {actionLoading === 'terminate' ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
            ) : (
              <Trash2 size={14} />
            )}
            <span>종료</span>
          </button>
        )}
      </div>

      {/* 최근 액션 정보 및 상세 히스토리 */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <ActionHistory 
          instanceId={instance.instanceId}
          compact={!showDetails}
          limit={showDetails ? 20 : 5}
          lazyLoad={!showDetails}
          preloadedData={{
            instanceId: instance.instanceId,
            state: instance.state,
            launchTime: instance.launchTime,
            lastModified: instance.metadata?.lastModified,
            notes: instance.metadata?.notes
          }}
        />
        
        {/* 상세 정보 토글 버튼 */}
        {/* <div className="mt-3">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <Activity size={16} />
            <span>{showDetails ? '히스토리 접기' : '전체 히스토리 보기'}</span>
          </button>
        </div> */}
        
        {/* 기존 메타데이터 정보 (상세 모드에서만 표시) */}
        {/* showDetails && */ (instance.metadata?.notes || instance.metadata?.lastModified) && (
          <div className="mt-4 p-3 bg-gray-50 rounded space-y-2 text-sm text-gray-600">
            {instance.metadata?.notes && (
              <div>
                <span className="font-medium">메모:</span> {instance.metadata.notes}
              </div>
            )}
            {instance.metadata?.lastModified && (
              <div>
                <span className="font-medium">최종 수정:</span> {formatDate(instance.metadata.lastModified)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstanceCard;